import React, { useState, useRef, useEffect } from "react";
import { Table, Switch, Button, notification, Modal } from "antd";
// import "antd/dist/antd.css";
import { useLoadScript } from "@react-google-maps/api";
import { useAppDispatch } from "@app/hooks/reduxHooks";
import { createGeoJSONAction, deleteGeoJSONAction, getGeoJSONDataAction, updateGeoJSONAction } from "@app/store/slices/userSlice";
import config from "@app/config";

const libraries = ["places"];

const API_BASE_URL = config.API1Url;

const GoogleMapsPage = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [toggle, setToggle] = useState("whitelisted");
  const [locations, setLocations] = useState([]); // List of saved locations
  const [selectedLocation, setSelectedLocation] = useState(null); // Currently selected location
  const [radius, setRadius] = useState(1000); // Default radius
  const [isEditing, setIsEditing] = useState(false); // Track if we are editing a location
  const [isEditAPI, setIsEditAPI] = useState(false); // Track if we are editing a location

  const dispatch = useAppDispatch();
  // Fetch saved locations on component mount
  useEffect(() => {
    fetchSavedLocations();
  }, []);

  const fetchSavedLocations = async () => {
    try {

      const response = await dispatch(getGeoJSONDataAction())
        .unwrap();
      const data = response;


      console.log('data-------', data);

      if (data && data.data) {
        setIsEditAPI(true);
        const mappedLocations = data.data.features.map((feature) => ({
          id: feature.properties.id,
          name: feature.properties.name,
          type: feature.properties.type,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
          radius: feature.radius,
        }));

        setLocations(mappedLocations);
        // notification.success({ message: "Locations loaded successfully!" });
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      notification.error({ message: "Failed to load locations." });
    }
  };

  useEffect(() => {
    if (!isLoaded || !window.google) return;

    // Initialize Map
    mapRef.current = new window.google.maps.Map(document.getElementById("map"), {
      center: { lat: 30.692499794031743, lng: 76.84418202771091 },
      zoom: 13,
    });

    // Initialize Autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      document.getElementById("autocomplete")
    );
    autocompleteRef.current.bindTo("bounds", mapRef.current);

    // Add place_changed listener
    autocompleteRef.current.addListener("place_changed", handlePlaceChanged);

    // Initialize Marker
    markerRef.current = new window.google.maps.Marker({
      map: mapRef.current,
      draggable: true,
    });

    // Initialize Circle
    circleRef.current = new window.google.maps.Circle({
      map: mapRef.current,
      radius: radius,
      fillColor: toggle === "whitelisted" ? "green" : "red",
      fillOpacity: 0.2,
      strokeColor: toggle === "whitelisted" ? "green" : "red",
      strokeWeight: 1,
    });

    // Sync marker and circle
    markerRef.current.addListener("drag", () => {
      const position = markerRef.current.getPosition();
      circleRef.current.setCenter(position);
    });

    return () => {
      // Cleanup listeners
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      if (markerRef.current) {
        window.google.maps.event.clearInstanceListeners(markerRef.current);
      }
    };
  }, [isLoaded, toggle]);

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.geometry || !place.geometry.location) {
      alert("No location data available for the selected place.");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    mapRef.current.setCenter({ lat, lng });
    markerRef.current.setPosition({ lat, lng });
    circleRef.current.setCenter({ lat, lng });

    setSelectedLocation({
      id: place.place_id,
      name: place.formatted_address || "Unknown Location",
      lat,
      lng,
      radius,
      type: toggle,
    });

    setIsEditing(false); // Reset editing state
  };

  const handleRadiusChange = (value) => {
    setRadius(value);
    circleRef.current.setRadius(value);

    if (selectedLocation) {
      setSelectedLocation((prev) => ({
        ...prev,
        radius: value,
      }));
    }
  };

  const handleAddOrUpdate = async () => {
    if (!selectedLocation) {
      alert("Please select a location first!");
      return;
    }

    const newLocations = isEditing
      ? locations.map((loc) =>
        loc.id === selectedLocation.id ? { ...selectedLocation } : loc
      )
      : [...locations, selectedLocation];

    setLocations(newLocations);

    try {
      const geoJsonData = {
        type: "FeatureCollection",
        features: newLocations.map((loc) => ({
          type: "Feature",
          properties: {
            id: loc.id,
            name: loc.name,
            type: loc.type,
          },
          geometry: {
            type: "Point",
            coordinates: [loc.lng, loc.lat],
          },
          radius: loc.radius,
        })),
      };

      const response = await dispatch(isEditAPI ? updateGeoJSONAction(geoJsonData) : createGeoJSONAction(geoJsonData)).unwrap();


      if (response.success) {
        notification.success({
          message: `Location ${isEditing ? "updated" : "saved"} successfully!`,
        });
        setSelectedLocation(null);
        setIsEditing(false);
        setIsEditAPI(true);
      } else {
        throw new Error("Failed to save data.");
      }
    } catch (error) {
      console.error("Error saving location:", error);
      notification.error({ message: "Failed to save location." });
    }
  };

  const handleEditRow = (location) => {
    setSelectedLocation(location);
    setRadius(location.radius);

    mapRef.current.setCenter({ lat: location.lat, lng: location.lng });
    markerRef.current.setPosition({ lat: location.lat, lng: location.lng });
    circleRef.current.setCenter({ lat: location.lat, lng: location.lng });
    circleRef.current.setRadius(location.radius);

    setIsEditing(true);
  };


  const handleDeleteRow =  (record) => {

    console.log('record',record)
    Modal.confirm({
      title: 'Are you sure?',
      content: `Are you sure to perform this action?`,
      onOk: async() => {

        try {



          const response = await dispatch(deleteGeoJSONAction(record?.id)).unwrap();


          if (response.success) {
            notification.success({
              message: `Location ${isEditing ? "updated" : "saved"} successfully!`,
            });
            setSelectedLocation(null);
           fetchSavedLocations();
          } else {
            throw new Error("Failed to save data.");
          }
        }
        catch (error) {
          console.error("Error saving location:", error);
          notification.error({ message: "Failed to save location." });
        }

      }

    })

  }

  const columns = [
    {
      title: "Location Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <span
          style={{
            color: text === "whitelisted" ? "green" : "red",
            fontWeight: "bold",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEditRow(record)}>
            Edit
          </Button>
          <Button type="link" onClick={() => handleDeleteRow(record)}>
            Delete
          </Button>

        </>
      ),
    },
  ];

  if (loadError) {
    return <div>Error loading Google Maps API: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "auto" }}>
      <h1 style={{ textAlign: "center" }}>Manage Areas</h1>

      {/* Toggle Button */}
      <div style={{ marginBottom: "20px", display: "flex", alignItems: "center" }}>
        <span style={{ marginRight: "10px", fontWeight: "bold" }}>
          Current Type:
        </span>
        <Switch
          checked={toggle === "whitelisted"}
          onChange={(checked) => setToggle(checked ? "whitelisted" : "blacklisted")}
          checkedChildren="Whitelisted"
          unCheckedChildren="Blacklisted"
        />
      </div>

      {/* Autocomplete Search Box */}
      <div style={{ marginBottom: "20px" }}>
        <input
          id="autocomplete"
          type="text"
          placeholder="Search for a place"
          style={{
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        />
      </div>

      {/* Map */}
      <div
        id="map"
        style={{ height: "700px", width: "100%", marginBottom: "20px" }}
      ></div>

      {/* Radius Adjustment */}
      {selectedLocation && (
        <div style={{ marginBottom: "20px" }}>
          <label>
            Adjust Radius:
            <input
              type="range"
              min="100"
              max="10000"
              step="100"
              value={radius}
              onChange={(e) => handleRadiusChange(parseInt(e.target.value, 10))}
            />
            <span> {radius} meters</span>
          </label>
          <Button
            type="primary"
            onClick={handleAddOrUpdate}
            style={{ marginLeft: "20px" }}
          >
            {isEditing ? "Update" : "Add to Table"}
          </Button>
        </div>
      )}

      {/* Display Table */}
      <Table
        dataSource={locations}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 5 }}
        bordered
      />
    </div>
  );
};

export default GoogleMapsPage;
